import { z } from "zod";
import { getBrowserEnvSource } from "@/lib/runtime-config";

const clientEnvSchema = z.object({
  VITE_SUPABASE_URL: z.string().url(),
  VITE_SUPABASE_ANON_KEY: z.string().min(1),
  VITE_SUPABASE_FUNCTIONS_URL: z.string().url().optional(),
  VITE_SITE_URL: z.string().url().optional(),
  VITE_IMPORT_FILE_SIZE_LIMIT_BYTES: z.coerce.number().int().positive().optional(),
  VITE_IMPORT_MAX_PAGE_COUNT: z.coerce.number().int().positive().optional(),
  VITE_STORY_IMPORT_PROGRESS_POLL_MS: z.coerce.number().int().positive().optional(),
});

export type ClientEnv = {
  importFileSizeLimitBytes: number;
  importMaxPageCount: number;
  siteUrl: string;
  storyImportProgressPollMs: number;
  supabaseUrl: string;
  supabaseFunctionsUrl: string;
  supabaseAnonKey: string;
};

export function isSupabaseConfigured() {
  const env = getBrowserEnvSource();

  return Boolean(
    env.VITE_SUPABASE_URL && env.VITE_SUPABASE_ANON_KEY,
  );
}

function isLocalHostname(hostname: string) {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function resolveSiteUrl(configuredSiteUrl: string | undefined) {
  const browserOrigin = typeof window === "undefined" ? null : window.location.origin;

  if (!configuredSiteUrl || !browserOrigin) {
    return configuredSiteUrl ?? browserOrigin ?? "http://127.0.0.1:4173";
  }

  const configuredHostname = new URL(configuredSiteUrl).hostname;
  const browserHostname = new URL(browserOrigin).hostname;

  if (isLocalHostname(configuredHostname) && !isLocalHostname(browserHostname)) {
    return browserOrigin;
  }

  return configuredSiteUrl;
}

export function getClientEnv(): ClientEnv {
  const parsed = clientEnvSchema.safeParse(getBrowserEnvSource());

  if (!parsed.success) {
    throw new Error("Supabase environment variables are missing or invalid.");
  }

  return {
    importFileSizeLimitBytes: parsed.data.VITE_IMPORT_FILE_SIZE_LIMIT_BYTES ?? 26_214_400,
    importMaxPageCount: parsed.data.VITE_IMPORT_MAX_PAGE_COUNT ?? 100,
    storyImportProgressPollMs: parsed.data.VITE_STORY_IMPORT_PROGRESS_POLL_MS ?? 4_000,
    supabaseUrl: parsed.data.VITE_SUPABASE_URL,
    supabaseFunctionsUrl: parsed.data.VITE_SUPABASE_FUNCTIONS_URL ?? parsed.data.VITE_SUPABASE_URL,
    supabaseAnonKey: parsed.data.VITE_SUPABASE_ANON_KEY,
    siteUrl: resolveSiteUrl(parsed.data.VITE_SITE_URL),
  };
}
